import React from "react";
import customError from "../images/customError.jpg";

import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Stack,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      timer: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  componentDidMount() {
    if (this.state.hasError) {
      this.startRedirectTimer();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.hasError && this.state.hasError) {
      this.startRedirectTimer();
    }
  }

  componentWillUnmount() {
    if (this.state.timer) {
      clearTimeout(this.state.timer);
    }
  }

  startRedirectTimer = () => {
    let countdown = 10;
    const timer = setInterval(() => {
      if (countdown > 0) {
        this.setState({ countdown: countdown - 1 });
        countdown--;
      } else {
        clearInterval(timer);
        if (window.location.pathname === "/dashboard") {
          window.location.href = "/";
        } else {
          window.location.href = "/dashboard";
        }
      }
    }, 1000);
    this.setState({ timer });
  };

  handleRefresh = () => {
    if (this.state.timer) {
      clearTimeout(this.state.timer);
    }
    window.location.reload();
  };

  handlePrevious = () => {
    if (this.state.timer) {
      clearTimeout(this.state.timer);
    }
    if (window.location.pathname === "/dashboard") {
      window.location.href = "/";
    } else {
      window.location.href = "/dashboard";
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: "100vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            overflow: "auto",
            py: 4,
          }}
        >
          <Container maxWidth="sm" sx={{ my: "auto" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                textAlign: "center",
                maxHeight: "90vh",
                overflow: "auto",
                boxShadow: "none",
              }}
            >
              <Box
                component="img"
                src={customError}
                alt="Error"
                sx={{
                  width: "100%",
                  maxWidth: 360,
                  height: "auto",
                  margin: "0 auto",
                  display: "block",
                  mb: 2,
                }}
              />

              <Typography
                variant="h5"
                color="error"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Oops! Something went wrong
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                We're sorry for the inconvenience. Please try refreshing the
                page or go back.
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You will be automatically redirected in {this.state.countdown} 
                seconds...
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                sx={{ mb: 2 }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                  sx={{
                    bgcolor: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                  }}
                >
                  Refresh Page
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={this.handlePrevious}
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    "&:hover": {
                      borderColor: "primary.dark",
                      bgcolor: "primary.lighter",
                    },
                  }}
                >
                  Go Back
                </Button>
              </Stack>
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
